/**
 * @module core/router/url-router
 * @description URL routing and parameter handling
 * Manages URL parameters, navigation, and browser history integration
 */

import { ENTITY_TYPES } from '../../features/entity/entity-types.js';
import { logger } from '../../utils/logger.js';

/**
 * Get entity information from URL parameters
 * Returns object with { type, id, config } or null if no entity specified
 * Priority order: projectId, opportunityId, leadId, contactId
 * @returns {{type: string, id: string, config: Object}|null} Entity info or null
 */
export function getEntityInfo() {
    const urlParams = new URLSearchParams(window.location.search);

    // Check entity-specific parameters in priority order
    for (const [entityType, config] of Object.entries(ENTITY_TYPES)) {
        const paramValue = urlParams.get(config.paramKey);

        // Ensure paramValue is not null, undefined, or empty string
        if (paramValue && paramValue.trim() !== '') {
            return {
                type: entityType,
                id: paramValue,
                config: config
            };
        }
    }

    return null; // No entity specified
}

/**
 * Get file ID from URL parameter
 * @returns {string|null} File ID or null if not present
 */
export function getFileId() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('fileId');
}

/**
 * Get current entity type from URL
 * @returns {string|null} Entity type or null
 */
export function getCurrentEntityType() {
    const entityInfo = getEntityInfo();
    return entityInfo?.type || null;
}

/**
 * Navigate to entity view
 * @param {string} entityType - Type of entity (projects, opportunities, leads, contacts)
 * @param {string} entityId - ID of the entity
 * @param {string|null} fileId - Optional file ID to highlight
 */
export function navigateToEntity(entityType, entityId, fileId = null) {
    const config = ENTITY_TYPES[entityType];
    if (!config) {
        logger.error(`Unknown entity type: ${entityType}`);
        return;
    }

    let url = `?${config.paramKey}=${encodeURIComponent(entityId)}`;
    if (fileId) {
        url += `&fileId=${encodeURIComponent(fileId)}`;
    }

    window.location.href = url;
}

/**
 * Update URL without page reload using History API
 * @param {string} entityType - Type of entity
 * @param {string} entityId - ID of the entity
 * @param {string|null} fileId - Optional file ID
 */
export function updateURL(entityType, entityId, fileId = null) {
    const config = ENTITY_TYPES[entityType];
    if (!config) {
        logger.error(`Unknown entity type: ${entityType}`);
        return;
    }

    let url = `?${config.paramKey}=${encodeURIComponent(entityId)}`;
    if (fileId) {
        url += `&fileId=${encodeURIComponent(fileId)}`;
    }

    window.history.pushState({ entityType, entityId, fileId }, '', url);
}

/**
 * Navigate to home page (entity selection)
 */
export function navigateToHome() {
    window.location.href = '?';
}

/**
 * Legacy function - use getEntityInfo() instead
 * Returns projectId if entity type is projects, otherwise null
 * @returns {string|null} Project ID or null
 * @deprecated Use getEntityInfo() instead
 */
export function getProjectId() {
    const entityInfo = getEntityInfo();
    return (entityInfo?.type === 'projects') ? entityInfo.id : null;
}

export default {
    getEntityInfo,
    getFileId,
    getCurrentEntityType,
    navigateToEntity,
    updateURL,
    navigateToHome,
    getProjectId
};
